import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}`;

const BulkActionModal = ({ userIds, users, onClose, onComplete }) => {
  const { token } = useAuth();
  const [selectedAction, setSelectedAction] = useState('');
  const [reason, setReason] = useState('');
  const [durationDays, setDurationDays] = useState(7);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const bulkActions = {
    suspend_users: {
      label: '⏸️ Suspend Users',
      description: 'Temporarily suspend selected users',
      color: 'bg-yellow-500 text-white',
      requiresReason: true,
      requiresDuration: true
    },
    ban_users: {
      label: '🚫 Ban Users',
      description: 'Permanently ban selected users',
      color: 'bg-red-500 text-white',
      requiresReason: true,
      requiresDuration: false
    },
    unban_users: {
      label: '🔓 Unban/Reactivate Users',
      description: 'Reactivate suspended or banned users',
      color: 'bg-green-500 text-white',
      requiresReason: false,
      requiresDuration: false
    },
    force_reset_password: {
      label: '🔄 Force Password Reset',
      description: 'Send password reset emails to selected users',
      color: 'bg-blue-500 text-white',
      requiresReason: false,
      requiresDuration: false
    },
    delete_users: {
      label: '🗑️ Delete Users',
      description: 'Permanently delete selected users (cannot be undone)',
      color: 'bg-red-700 text-white',
      requiresReason: false,
      requiresDuration: false
    }
  };

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setShowConfirmation(false);
    setResults(null);
  };

  const handleConfirmAction = () => {
    const action = bulkActions[selectedAction];
    if (action.requiresReason && !reason.trim()) {
      alert('Please provide a reason for this action.');
      return;
    }
    setShowConfirmation(true);
  };

  const executeAction = async () => {
    if (confirmText !== 'CONFIRM') {
      alert('Please type "CONFIRM" to proceed with this bulk action.');
      return;
    }

    setLoading(true);
    try {
      const parameters = {};
      
      if (bulkActions[selectedAction].requiresReason) {
        parameters.reason = reason;
      }
      
      if (bulkActions[selectedAction].requiresDuration) {
        parameters.duration_days = durationDays;
      }

      const response = await axios.post(`${API}/api/admin/users/bulk-action`, {
        action: selectedAction,
        user_ids: userIds,
        suspension_days: durationDays,
        reason: reason || "Admin action"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResults(response.data.results || { success: [], errors: [] });
      setShowConfirmation(false);
    } catch (error) {
      console.error('Error executing bulk action:', error);
      alert('Failed to execute bulk action');
    } finally {
      setLoading(false);
    }
  };

  const getActionDescription = () => {
    const action = bulkActions[selectedAction];
    if (!action) return '';
    
    let description = `You are about to ${action.label.replace(/[^\w\s]/gi, '').toLowerCase()} ${userIds.length} user${userIds.length !== 1 ? 's' : ''}.`;
    
    if (action.requiresDuration) {
      description += ` Duration: ${durationDays} days.`;
    }
    
    if (action.requiresReason) {
      description += ` Reason: "${reason}"`;
    }
    
    return description;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              📦 Bulk Actions - {userIds.length} Users Selected
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {/* Results Display */}
          {results && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Action Results</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="mb-4">
                  <span className="text-green-600 font-medium">
                    ✅ {results.success.length} successful
                  </span>
                  {results.errors.length > 0 && (
                    <span className="ml-4 text-red-600 font-medium">
                      ❌ {results.errors.length} failed
                    </span>
                  )}
                </div>

                {results.success.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-green-800 mb-2">Successful Actions:</h4>
                    <div className="space-y-1 text-sm">
                      {results.success.map((msg, index) => (
                        <div key={index} className="text-green-700">✅ {msg}</div>
                      ))}
                    </div>
                  </div>
                )}

                {results.errors.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-800 mb-2">Failed Actions:</h4>
                    <div className="space-y-1 text-sm">
                      {results.errors.map((msg, index) => (
                        <div key={index} className="text-red-700">❌ {msg}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      onComplete();
                      onClose();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    ✅ Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Selection */}
          {!results && !showConfirmation && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Select Bulk Action</h3>
                <div className="space-y-3">
                  {Object.entries(bulkActions).map(([actionKey, action]) => (
                    <label key={actionKey} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="bulkAction"
                        value={actionKey}
                        checked={selectedAction === actionKey}
                        onChange={() => handleActionSelect(actionKey)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${action.color} mb-1`}>
                          {action.label}
                        </div>
                        <p className="text-sm text-gray-600">{action.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Action Parameters */}
              {selectedAction && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Action Parameters</h3>

                  {/* Duration for suspension */}
                  {bulkActions[selectedAction].requiresDuration && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Suspension Duration (days) *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={durationDays}
                        onChange={(e) => setDurationDays(parseInt(e.target.value) || 1)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Users will be suspended for this many days</p>
                    </div>
                  )}

                  {/* Reason */}
                  {bulkActions[selectedAction].requiresReason && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason *
                      </label>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for this action..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                      />
                    </div>
                  )}

                  {!bulkActions[selectedAction].requiresReason && !bulkActions[selectedAction].requiresDuration && (
                    <p className="text-gray-600 text-sm">No additional parameters required for this action.</p>
                  )}
                </div>
              )}

              {/* Selected Users Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Selected Users ({userIds.length})</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                  <div className="space-y-2">
                    {users.map(user => (
                      <div key={user.id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{user.username}</span>
                          <span className="text-gray-500 ml-2">({user.email})</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' :
                          user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          user.status === 'banned' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Confirmation Step */}
          {showConfirmation && !results && (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">⚠️</span>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Confirm Bulk Action</h3>
                    <p className="text-red-700 mb-4">{getActionDescription()}</p>
                    
                    <div className="bg-white border border-red-200 rounded p-3 mb-4">
                      <p className="text-sm font-medium text-red-800 mb-2">
                        ⚠️ This action will affect {userIds.length} users and cannot be undone automatically.
                      </p>
                      <p className="text-sm text-red-600">
                        Each affected user will need to be managed individually to reverse these changes.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        Type "CONFIRM" to proceed:
                      </label>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Type CONFIRM here"
                        className="w-full px-3 py-2 border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          {!showConfirmation && !results && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                ❌ Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={!selectedAction || (bulkActions[selectedAction]?.requiresReason && !reason.trim())}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ➡️ Continue
              </button>
            </>
          )}

          {showConfirmation && !loading && !results && (
            <>
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                ⬅️ Back
              </button>
              <button
                onClick={executeAction}
                disabled={confirmText !== 'CONFIRM'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔴 Execute Action
              </button>
            </>
          )}

          {loading && (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-gray-600">Processing...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkActionModal;